import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { VRButton } from 'three/addons/webxr/VRButton.js';

import {
  APP_CONFIG,
  DESKTOP_EYE_HEIGHT,
  QUALITY_PRESETS,
  START_POSITION,
  START_ROTATION,
  XR_CONFIG,
} from './config.js';
import { HotspotManager } from './interactions/HotspotManager.js';
import { SceneManager } from './scene/SceneManager.js';
import { InfoPanel } from './ui/InfoPanel.js';
import { NarrationAudio } from './utils/audio.js';
import { createLoadingManager } from './utils/loaders.js';
import {
  configureRenderer,
  PerformanceMonitor,
  selectQualityLevel,
} from './utils/performance.js';
import { Controllers } from './xr/Controllers.js';
import { Locomotion } from './xr/Locomotion.js';
import { Teleportation } from './xr/Teleportation.js';
import { XRManager, UNSUPPORTED_MESSAGE } from './xr/XRManager.js';

const DESKTOP_START = Object.freeze([0, 6.4, 27]);
const DESKTOP_TARGET = Object.freeze([0, 4.2, -2]);
const DESKTOP_MOVE_SPEED = 5;

function isDebugEnabled() {
  return new URLSearchParams(window.location.search).get('debug') === 'true';
}

function localizeThreeVRButton(button) {
  if (!button) return () => {};

  const labels = new Map([
    ['ENTER VR', 'ENTRAR EN REALIDAD VIRTUAL'],
    ['EXIT VR', 'SALIR DE REALIDAD VIRTUAL'],
    ['VR NOT SUPPORTED', 'WEBXR NO DISPONIBLE'],
    ['VR NOT ALLOWED', 'WEBXR NO AUTORIZADO'],
    ['WEBXR NEEDS HTTPS', 'WEBXR REQUIERE HTTPS'],
    ['WEBXR NOT AVAILABLE', 'WEBXR NO DISPONIBLE'],
  ]);

  const translate = () => {
    const translated = labels.get(button.textContent?.trim());
    if (translated && button.textContent !== translated) button.textContent = translated;
  };

  translate();
  const observer = new MutationObserver(translate);
  observer.observe(button, { childList: true, subtree: true, characterData: true });
  return () => observer.disconnect();
}

export class App {
  constructor(root) {
    if (!root) throw new Error('App requiere un elemento raíz.');

    this.root = root;
    this.qualityName = selectQualityLevel();
    this.quality = QUALITY_PRESETS[this.qualityName];
    this.debugEnabled = isDebugEnabled();
    this.ready = false;
    this.desktopStarted = false;
    this.disposed = false;
    this._loadingFinished = false;

    this._forward = new THREE.Vector3();
    this._right = new THREE.Vector3();
    this._desktopMovement = new THREE.Vector3();
    this._desktopCandidate = new THREE.Vector3();
    this._keys = new Set();
    this.clock = new THREE.Clock();

    this._onResize = this.resize.bind(this);
    this._onKeyDown = this.handleKeyDown.bind(this);
    this._onKeyUp = this.handleKeyUp.bind(this);
    this._render = this.render.bind(this);
  }

  async initialize() {
    this.buildShell();
    this.createRenderer();
    this.createCameras();

    const loadingManager = createLoadingManager({
      onProgress: ({ percentage }) => this.setLoadingProgress(percentage),
      onError: (url) => console.error(`No se ha podido cargar el recurso: ${url}`),
    });

    this.sceneManager = new SceneManager(this.renderer, {
      quality: this.qualityName,
      loadingManager,
    });
    this.scene = this.sceneManager.scene;
    this.scene.add(this.playerRig);

    this.createDesktopControls();
    this.renderer.setAnimationLoop(this._render);
    window.addEventListener('resize', this._onResize);
    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    this.resize();

    await this.sceneManager.initialize();
    this.setLoadingProgress(100);
    this.createInteractions(loadingManager);
    this.createXRSystems();

    if (this.debugEnabled) {
      this.performanceMonitor = new PerformanceMonitor(
        this.renderer,
        this.desktopCamera,
        this.playerRig,
      );
    }

    this.ready = true;
    this.enableLandingActions();

    if (this.sceneManager.modelStatus?.usedPlaceholder) {
      this.showToast(`${APP_CONFIG.modelErrorMessage} Se utiliza el modelo provisional.`);
    }

    window.setTimeout(() => this.finishLoading(), 360);
    return this;
  }

  buildShell() {
    this.root.innerHTML = `
      <div class="experience-shell">
        <div class="canvas-stage" data-canvas-stage></div>

        <section class="loading-screen" data-loading-screen aria-live="polite" aria-label="Carga de la experiencia">
          <div class="loading-mark" aria-hidden="true"><span></span><span></span><span></span></div>
          <p>Cargando experiencia…</p>
          <strong data-loading-value>0 %</strong>
          <div class="loading-track" aria-hidden="true"><span data-loading-bar></span></div>
        </section>

        <section class="landing" data-landing aria-labelledby="experience-title">
          <div class="landing-grid" aria-hidden="true"></div>
          <header class="landing-meta">
            <span>WebXR · 6DoF</span>
            <span>San Esteban de Gormaz</span>
          </header>
          <div class="landing-copy">
            <p class="eyebrow">${APP_CONFIG.experienceLabel}</p>
            <h1 id="experience-title">${APP_CONFIG.title}</h1>
            <p class="location">${APP_CONFIG.location}</p>
            <p class="intro">Recorre el exterior a escala real y descubre sus puntos de interés en una visita inmersiva.</p>
            <div class="landing-actions">
              <div class="xr-button-slot" data-xr-button-slot></div>
              <button class="button-secondary" type="button" data-explore disabled>
                Explorar desde el navegador
              </button>
            </div>
            <p class="quest-note">Utiliza tus Meta Quest 3 para explorar el exterior de la iglesia.</p>
            <p class="xr-status" data-xr-status role="status" aria-live="polite"></p>
          </div>
          <div class="landing-scale" aria-hidden="true">
            <span>1:1</span>
            <small>Escala real</small>
          </div>
        </section>

        <aside class="desktop-hud" data-desktop-hud hidden>
          <div class="hud-title">
            <span>Visita exterior</span>
            <strong>${APP_CONFIG.title}</strong>
          </div>
          <div class="hud-status">
            <span class="status-dot" aria-hidden="true"></span>
            Calidad ${this.qualityName}
          </div>
          <div class="desktop-help">
            <span><kbd>Ratón</kbd> Mirar</span>
            <span><kbd>Rueda</kbd> Acercar</span>
            <span><kbd>W A S D</kbd> Mover</span>
            <span><kbd>R</kbd> Volver al inicio</span>
          </div>
          <button class="reset-button" type="button" data-reset>Volver al inicio</button>
        </aside>

        <div class="toast" data-toast role="status" aria-live="polite" hidden></div>
        <div class="fatal-error" data-fatal hidden></div>
      </div>
    `;

    this.canvasStage = this.root.querySelector('[data-canvas-stage]');
    this.loadingScreen = this.root.querySelector('[data-loading-screen]');
    this.loadingValue = this.root.querySelector('[data-loading-value]');
    this.loadingBar = this.root.querySelector('[data-loading-bar]');
    this.landing = this.root.querySelector('[data-landing]');
    this.exploreButton = this.root.querySelector('[data-explore]');
    this.xrButtonSlot = this.root.querySelector('[data-xr-button-slot]');
    this.xrStatus = this.root.querySelector('[data-xr-status]');
    this.desktopHud = this.root.querySelector('[data-desktop-hud]');
    this.resetButton = this.root.querySelector('[data-reset]');
    this.toast = this.root.querySelector('[data-toast]');
    this.fatalError = this.root.querySelector('[data-fatal]');

    this.exploreButton.addEventListener('click', () => this.startDesktop());
    this.resetButton.addEventListener('click', () => this.resetPlayerPosition());
  }

  createRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      antialias: this.quality.antialias,
      alpha: false,
      depth: true,
      stencil: false,
      powerPreference: 'high-performance',
    });
    configureRenderer(this.renderer, this.qualityName);
    this.renderer.xr.setReferenceSpaceType(XR_CONFIG.referenceSpaceType);
    this.renderer.domElement.className = 'experience-canvas';
    this.renderer.domElement.setAttribute('aria-label', 'Vista tridimensional del exterior de la iglesia');
    this.canvasStage.append(this.renderer.domElement);
  }

  createCameras() {
    this.playerRig = new THREE.Group();
    this.playerRig.name = 'playerRig';
    this.playerRig.position.fromArray(START_POSITION);
    this.playerRig.rotation.y = START_ROTATION;

    this.xrCamera = new THREE.PerspectiveCamera(58, 1, 0.05, 180);
    this.xrCamera.name = 'xrCamera';
    this.xrCamera.position.set(0, DESKTOP_EYE_HEIGHT, 0);
    this.playerRig.add(this.xrCamera);

    this.desktopCamera = new THREE.PerspectiveCamera(54, 1, 0.1, 180);
    this.desktopCamera.name = 'desktopCamera';
    this.desktopCamera.position.fromArray(DESKTOP_START);
  }

  createDesktopControls() {
    this.desktopControls = new OrbitControls(this.desktopCamera, this.renderer.domElement);
    this.desktopControls.target.fromArray(DESKTOP_TARGET);
    this.desktopControls.enableDamping = true;
    this.desktopControls.dampingFactor = 0.07;
    this.desktopControls.enablePan = true;
    this.desktopControls.minDistance = 3;
    this.desktopControls.maxDistance = 62;
    this.desktopControls.maxPolarAngle = Math.PI * 0.49;
    this.desktopControls.enabled = false;
    this.desktopControls.update();
  }

  createInteractions(loadingManager) {
    this.audio = new NarrationAudio(this.xrCamera, loadingManager);
    this.infoPanel = new InfoPanel({
      audioController: this.audio,
      onReset: () => this.resetPlayerPosition(),
    });
    this.scene.add(this.infoPanel);

    this.hotspotManager = new HotspotManager({
      scene: this.scene,
      camera: this.desktopCamera,
      infoPanel: this.infoPanel,
      domElement: this.renderer.domElement,
    });
  }

  createXRSystems() {
    this.controllers = new Controllers({
      renderer: this.renderer,
      playerRig: this.playerRig,
      rayLength: XR_CONFIG.rayLength,
      onSelectStart: (state) => this.handleXRSelection(state),
    });

    const navigation = this.sceneManager.getNavigationData();
    this.teleportation = new Teleportation({
      renderer: this.renderer,
      playerRig: this.playerRig,
      camera: this.xrCamera,
      controllers: this.controllers,
      teleportableMeshes: navigation.teleportableMeshes,
      bounds: navigation.walkableBounds,
      blockedZones: navigation.blockedBounds,
      validator: navigation.isDestinationValid,
      bindSelect: false,
      onTeleport: () => this.infoPanel.close(),
    });

    this.locomotion = new Locomotion({
      renderer: this.renderer,
      camera: this.xrCamera,
      playerRig: this.playerRig,
      controllers: this.controllers,
      movementValidator: (point) => this.teleportation.isPointAllowed(point),
      onReset: () => this.resetPlayerPosition(),
    });

    const sessionInit = {
      requiredFeatures: [],
      optionalFeatures: [...new Set([
        XR_CONFIG.referenceSpaceType,
        ...XR_CONFIG.optionalFeatures,
      ])],
    };
    this.threeVRButton = VRButton.createButton(this.renderer, sessionInit);
    this.threeVRButton.id = 'xr-entry-button';
    this.threeVRButton.classList.add('button-primary', 'xr-entry-button');
    this.threeVRButton.removeAttribute('style');
    this.xrButtonSlot.append(this.threeVRButton);
    this.stopLocalizingVRButton = localizeThreeVRButton(this.threeVRButton);

    this.xrManager = new XRManager({
      renderer: this.renderer,
      camera: this.xrCamera,
      scene: this.scene,
      playerRig: this.playerRig,
      desktopControls: this.desktopControls,
      autoCreateButton: false,
      applyStartTransform: false,
      onSessionStart: () => this.handleXRStart(),
      onSessionEnd: () => this.handleXREnd(),
      onUnsupported: (message) => this.setXRStatus(message),
      onError: () => this.setXRStatus('No se ha podido iniciar la sesión de realidad virtual.'),
    });

    this.xrManager.checkSupport().then((supported) => {
      if (!supported) this.setXRStatus(UNSUPPORTED_MESSAGE);
    });
  }

  enableLandingActions() {
    this.exploreButton.disabled = false;
    this.threeVRButton?.removeAttribute('aria-disabled');
  }

  finishLoading() {
    if (this._loadingFinished || !this.ready) return;
    this._loadingFinished = true;
    this.loadingScreen.classList.add('is-finished');
    window.setTimeout(() => {
      this.loadingScreen.hidden = true;
    }, 650);
  }

  setLoadingProgress(percentage) {
    const value = Math.max(0, Math.min(100, Math.round(percentage || 0)));
    if (this.loadingValue) this.loadingValue.textContent = `${value} %`;
    if (this.loadingBar) this.loadingBar.style.transform = `scaleX(${value / 100})`;
  }

  setXRStatus(message) {
    if (this.xrStatus) this.xrStatus.textContent = message || '';
  }

  startDesktop() {
    if (!this.ready) return;
    this.desktopStarted = true;
    this.landing.classList.add('is-hidden');
    this.desktopHud.hidden = false;
    this.desktopControls.enabled = true;
    this.performanceMonitor?.setVisible(true);
    this.clock.getDelta();
  }

  handleXRStart() {
    this.landing.classList.add('is-hidden');
    this.desktopHud.hidden = true;
    this.desktopControls.enabled = false;
    this.performanceMonitor?.setVisible(false);
    this.infoPanel.close();
    this.controllers.setRaysVisible(true);
    this.resetPlayerPosition({ notify: false, desktop: false });
    this.clock.getDelta();
  }

  handleXREnd() {
    this.teleportation.clearAims();
    this.infoPanel.close();
    this.xrCamera.position.set(0, DESKTOP_EYE_HEIGHT, 0);
    this.xrCamera.quaternion.identity();
    this.landing.classList.remove('is-hidden');
    this.desktopHud.hidden = true;
    this.desktopControls.enabled = false;
    this.performanceMonitor?.setVisible(false);
  }

  handleXRSelection(state) {
    if (!this.renderer.xr.isPresenting) return;
    const selection = this.hotspotManager.selectFromController(state.controller, this.xrCamera);
    if (!selection) this.teleportation.tryTeleport(state.index);
  }

  resetPlayerPosition({ notify = true, desktop = true } = {}) {
    this.playerRig.position.fromArray(START_POSITION);
    this.playerRig.rotation.set(0, START_ROTATION, 0);
    this.playerRig.updateMatrixWorld(true);
    this.teleportation?.clearAims();
    this.infoPanel?.close();

    if (desktop && !this.renderer.xr.isPresenting) {
      this.desktopCamera.position.fromArray(DESKTOP_START);
      this.desktopControls.target.fromArray(DESKTOP_TARGET);
      this.desktopControls.update();
    }

    if (notify) this.showToast('Has vuelto al punto inicial de la visita.');
  }

  handleKeyDown(event) {
    if (event.repeat && event.code === 'KeyR') return;
    if (event.code === 'KeyR') {
      this.resetPlayerPosition();
      return;
    }
    this._keys.add(event.code);
  }

  handleKeyUp(event) {
    this._keys.delete(event.code);
  }

  updateDesktopMovement(delta) {
    if (!this.desktopStarted || !this.desktopControls.enabled) return;

    const horizontal = Number(this._keys.has('KeyD')) - Number(this._keys.has('KeyA'));
    const vertical = Number(this._keys.has('KeyW')) - Number(this._keys.has('KeyS'));
    if (horizontal === 0 && vertical === 0) return;

    this.desktopCamera.getWorldDirection(this._forward);
    this._forward.y = 0;
    if (this._forward.lengthSq() < 0.0001) return;
    this._forward.normalize();
    this._right.set(-this._forward.z, 0, this._forward.x);

    this._desktopMovement.copy(this._forward).multiplyScalar(vertical);
    this._desktopMovement.addScaledVector(this._right, horizontal);
    this._desktopMovement.normalize().multiplyScalar(DESKTOP_MOVE_SPEED * delta);

    this._desktopCandidate.copy(this.desktopControls.target).add(this._desktopMovement);
    if (!this.sceneManager.isTeleportDestinationValid(this._desktopCandidate, 0.8)) return;

    this.desktopCamera.position.add(this._desktopMovement);
    this.desktopControls.target.add(this._desktopMovement);
  }

  showToast(message) {
    window.clearTimeout(this._toastTimeout);
    this.toast.textContent = message;
    this.toast.hidden = false;
    this.toast.classList.add('is-visible');
    this._toastTimeout = window.setTimeout(() => {
      this.toast.classList.remove('is-visible');
      window.setTimeout(() => {
        this.toast.hidden = true;
      }, 250);
    }, 4800);
  }

  showFatalError(error) {
    console.error('No se ha podido iniciar la experiencia.', error);
    if (!this.fatalError) return;
    this.loadingScreen.hidden = true;
    this.fatalError.hidden = false;
    this.fatalError.innerHTML = `
      <strong>No se ha podido iniciar la experiencia.</strong>
      <span>Actualiza la página o prueba con un navegador compatible con WebGL.</span>
    `;
  }

  resize() {
    if (!this.renderer) return;
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.renderer.setSize(width, height, false);
    this.desktopCamera.aspect = width / height;
    this.desktopCamera.updateProjectionMatrix();
    this.xrCamera.aspect = width / height;
    this.xrCamera.updateProjectionMatrix();
  }

  render(time) {
    if (this.disposed || !this.scene) return;
    const delta = Math.min(this.clock.getDelta(), 0.1);
    const inXR = this.renderer.xr.isPresenting;

    if (this.performanceMonitor && !inXR) this.performanceMonitor.begin();

    if (inXR) {
      this.locomotion?.update(delta);
      this.teleportation?.update();
      this.hotspotManager?.update(this.xrCamera);
      this.hotspotManager?.updateFromXRControllers(this.controllers.states);
      this.renderer.render(this.scene, this.xrCamera);
    } else {
      this.updateDesktopMovement(delta);
      this.desktopControls?.update();
      this.hotspotManager?.update(this.desktopCamera);
      this.renderer.render(this.scene, this.desktopCamera);
    }

    if (this.performanceMonitor && !inXR) this.performanceMonitor.end(time);
  }

  dispose() {
    this.disposed = true;
    this.renderer?.setAnimationLoop(null);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    window.clearTimeout(this._toastTimeout);
    this.stopLocalizingVRButton?.();
    this.performanceMonitor?.dispose();
    this.hotspotManager?.dispose({ disposeSharedResources: true });
    this.infoPanel?.dispose();
    this.audio?.dispose();
    this.teleportation?.dispose();
    this.locomotion?.dispose();
    this.controllers?.dispose();
    this.xrManager?.dispose();
    this.desktopControls?.dispose();
    this.sceneManager?.dispose();
    this.renderer?.dispose();
  }
}

export default App;
